import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormResetField,
  useWatch,
} from 'react-hook-form';

import { StyledSwitch } from '@/components/Form/Form.styled';
import { GroupedSelectFormField } from '@/components/Form/GroupedSelect';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';
import { Icon } from '@/components/Icon/Icon';
import { ToolTipWrapper } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.styled';

import { useTranslate } from '@/hooks/useTranslate';

import { TaxNumberTypeCode } from '@/types/api/tax-info';
import { Option } from '@/types/components/common';

import { getDataTestId } from '@/utils/browser';
import { isNil } from '@/utils/common';

import { ForeignNoTaxNumberReasonSection } from '../ForeignNoTaxNumberReasonSection/ForeignNoTaxNumberReasonSection';
import { resetForeignNoTaxNumberReasonFields } from '../ForeignNoTaxNumberReasonSection/utils';
import { OtherCountriesTaxFormValues } from '../utils/types';
import { FOREIGN_TAX_OTHER_NUMBER_TYPE_MAX_LENGTH } from './constants';
import { getForeignTaxNumberTypeGroupedSelectConfig } from './utils';

import {
  TaxInfoDropdownRow,
  TaxInfoFieldLabel,
  TaxInfoQuestionSection,
  TaxInfoResidenceBlock,
  TaxInfoSwitchRow,
} from '../TaxInfoDrawer.styled';

type ForeignTaxResidencyBlockProps = {
  index: number;
  total: number;
  control: Control<OtherCountriesTaxFormValues>;
  errors: FieldErrors<OtherCountriesTaxFormValues>;
  countryOptions: Option[];
  isPending: boolean;
  resetField: UseFormResetField<OtherCountriesTaxFormValues>;
  onDelete: () => void;
};

export function ForeignTaxResidencyBlock({
  index,
  total,
  control,
  errors,
  countryOptions,
  isPending,
  resetField,
  onDelete,
}: Readonly<ForeignTaxResidencyBlockProps>) {
  const { t } = useTranslate();
  const blockErrors = errors.foreignTaxResidencies?.[index];

  const { groups, otherOption } = useMemo(
    () => getForeignTaxNumberTypeGroupedSelectConfig(t),
    [t],
  );

  const noTaxNumber = useWatch({
    control,
    name: `foreignTaxResidencies.${index}.noTaxNumber`,
  });

  const taxNumberTypeCode = useWatch({
    control,
    name: `foreignTaxResidencies.${index}.taxNumberTypeCode`,
  });

  const showOtherTaxNumberTypeField =
    taxNumberTypeCode === TaxNumberTypeCode.OTHER;

  const taxNumberFieldsDisabled = isPending || !!noTaxNumber;

  const taxNumberLabel = taxNumberTypeCode
    ? t('taxInfoDrawer.question2.foreignTaxResidency.taxNumber.labelWithType', {
        type: t(
          `taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.options.${taxNumberTypeCode}`,
        ),
      })
    : t('taxInfoDrawer.question2.foreignTaxResidency.taxNumber.label');

  const handleNoTaxNumberChange = (checked: boolean) => {
    if (checked) {
      resetField(`foreignTaxResidencies.${index}.taxNumberTypeCode`, {
        defaultValue: '',
      });
      resetField(`foreignTaxResidencies.${index}.otherTaxNumberType`, {
        defaultValue: '',
      });
      resetField(`foreignTaxResidencies.${index}.taxNumberValue`, {
        defaultValue: '',
      });
      return;
    }

    resetForeignNoTaxNumberReasonFields(resetField, index);
  };

  return (
    <TaxInfoQuestionSection>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="body1Medium">
          {t('taxInfoDrawer.question2.foreignTaxResidency.blockTitle')}
        </Typography>
        {total > 1 && (
          <IconButton
            onClick={onDelete}
            disabled={isPending}
            sx={{ width: 24, height: 24 }}
            title={t('taxInfoDrawer.question2.foreignTaxResidency.deleteBlock')}
            {...getDataTestId(`tax-info-foreign-tax-residency-delete-${index}`)}
          >
            <Icon name="delete" size={16} />
          </IconButton>
        )}
      </Box>

      <TaxInfoResidenceBlock>
        <Select
          control={control}
          name={`foreignTaxResidencies.${index}.countryCode`}
          options={countryOptions}
          placeholder={t(
            'taxInfoDrawer.question2.foreignTaxResidency.country.placeholder',
          )}
          error={!!blockErrors?.countryCode}
          helperText={blockErrors?.countryCode?.message}
          fullWidth
          disabled={isPending}
          useDrawerOnMobile
          withSearchBar
          {...getDataTestId(`tax-info-foreign-tax-residency-country-${index}`)}
        />

        <Box display="flex" flexDirection="column" gap={1}>
          <TaxInfoFieldLabel variant="caption">
            {t(
              'taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.label',
            )}
          </TaxInfoFieldLabel>

          <TaxInfoDropdownRow>
            <Box flex={1}>
              <GroupedSelectFormField
                control={control}
                name={`foreignTaxResidencies.${index}.taxNumberTypeCode`}
                selectionMode="single"
                title={t(
                  'taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.drawerTitle',
                )}
                placeholder={t(
                  'taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.placeholder',
                )}
                groups={groups}
                otherOption={otherOption}
                disabled={taxNumberFieldsDisabled}
                error={!!blockErrors?.taxNumberTypeCode}
                helperText={blockErrors?.taxNumberTypeCode?.message}
                onSelectChange={selectedValue => {
                  if (selectedValue !== TaxNumberTypeCode.OTHER) {
                    resetField(
                      `foreignTaxResidencies.${index}.otherTaxNumberType`,
                      { defaultValue: '' },
                    );
                  }

                  resetField(`foreignTaxResidencies.${index}.taxNumberValue`, {
                    defaultValue: '',
                  });
                }}
                {...getDataTestId(
                  `tax-info-foreign-tax-residency-type-${index}`,
                )}
              />
            </Box>
            <Tooltip
              title={t(
                'taxInfoDrawer.question2.foreignTaxResidency.taxNumberType.tooltip',
              )}
              placement="top"
              arrow
            >
              <ToolTipWrapper>
                <Icon
                  name="info-outline"
                  color={theme => theme.palette.neutral[700]}
                />
              </ToolTipWrapper>
            </Tooltip>
          </TaxInfoDropdownRow>
        </Box>

        {showOtherTaxNumberTypeField && (
          <TextInput
            name={`foreignTaxResidencies.${index}.otherTaxNumberType`}
            control={control}
            label={t(
              'taxInfoDrawer.question2.foreignTaxResidency.otherTaxNumberType.label',
            )}
            placeholder={t(
              'taxInfoDrawer.question2.foreignTaxResidency.otherTaxNumberType.placeholder',
            )}
            disabled={taxNumberFieldsDisabled}
            error={!!blockErrors?.otherTaxNumberType}
            inputProps={{
              maxLength: FOREIGN_TAX_OTHER_NUMBER_TYPE_MAX_LENGTH,
            }}
            {...getDataTestId(
              `tax-info-foreign-tax-residency-other-type-${index}`,
            )}
          />
        )}

        <TextInput
          inputMode={
            taxNumberTypeCode === TaxNumberTypeCode.OTHER ? 'text' : 'numeric'
          }
          name={`foreignTaxResidencies.${index}.taxNumberValue`}
          control={control}
          label={taxNumberLabel}
          placeholder={t(
            'taxInfoDrawer.question2.foreignTaxResidency.taxNumber.placeholder',
          )}
          disabled={
            taxNumberFieldsDisabled ||
            isNil(taxNumberTypeCode) ||
            !taxNumberTypeCode
          }
          error={!!blockErrors?.taxNumberValue}
          helperText={blockErrors?.taxNumberValue?.message}
          {...getDataTestId(`tax-info-foreign-tax-residency-number-${index}`)}
        />

        <TaxInfoSwitchRow>
          <Typography variant="body2">
            {t('taxInfoDrawer.question2.foreignTaxResidency.noTaxNumber.label')}
          </Typography>
          <Controller
            control={control}
            name={`foreignTaxResidencies.${index}.noTaxNumber`}
            render={({ field: { value, onChange } }) => (
              <StyledSwitch
                checked={!!value}
                disabled={isPending}
                onChange={(_event, checked) => {
                  onChange(checked);
                  handleNoTaxNumberChange(checked);
                }}
                {...getDataTestId(
                  `tax-info-foreign-tax-residency-no-number-${index}`,
                )}
              />
            )}
          />
        </TaxInfoSwitchRow>

        {noTaxNumber && (
          <ForeignNoTaxNumberReasonSection
            index={index}
            control={control}
            errors={errors}
            isPending={isPending}
            resetField={resetField}
          />
        )}
      </TaxInfoResidenceBlock>
    </TaxInfoQuestionSection>
  );
}
